"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.roundSecondsToNearestMinute = exports.intTimeToSecondsSinceMidnight = exports.timeStrToSecondsSinceMidnight = exports.toXXhrYYminZZsec = exports.hoursToSeconds = exports.minutesToSeconds = exports.minutesToHoursDecimal = exports.minutesToHours = exports.secondsToHoursDecimal = exports.secondsToHours = exports.secondsToMinutesDecimal = exports.secondsToMinutes = exports.timeStrToDecimalHour = exports.decimalHourToTimeStr = exports.secondsSinceMidnightToTimeStr = void 0;
/*
 * Copyright 2022, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const isFinite_1 = __importDefault(require("lodash/isFinite"));
const isString_1 = __importDefault(require("lodash/isString"));
const padStart_1 = __importDefault(require("lodash/padStart"));
const LodashExtensions_1 = require("./LodashExtensions");
/**
 * Formats the number of seconds since midnight to a time string (HH:MM[:ss]).
 * If seconds is larger than a 24 hour day perior, the timed string will be >
 * 24:00. This may happen for example for schedules for a day that end after
 * midnight.
 * @param secondsSinceMidnight Number of seconds since midnight
 * @param has24hours Whether the time should be formatted in 24hours format
 * (default true)
 * @param withSeconds Whether the time should include the number of seconds
 * (default false)
 * @returns A string formatted as (HH:MM[:ss]), or empty if seconds are invalid
 */
const secondsSinceMidnightToTimeStr = function (secondsSinceMidnight, has24hours = true, withSeconds = false) {
    if (!(0, isFinite_1.default)(secondsSinceMidnight)) {
        return '';
    }
    const secondsSinceMidnightAbs = secondsSinceMidnight >= 0 ? secondsSinceMidnight : 24 * 3600 - Math.abs(secondsSinceMidnight % (24 * 3600));
    let hour = Math.floor(secondsSinceMidnightAbs / 3600);
    const minute = Math.floor(secondsSinceMidnightAbs / 60 - hour * 60);
    if (!has24hours && hour > 12) {
        hour = hour - 12;
    }
    else if (!has24hours && hour === 0) {
        hour = 12;
    }
    if (withSeconds) {
        const second = secondsSinceMidnightAbs - hour * 3600 - minute * 60;
        return `${hour}:${(0, padStart_1.default)(String(minute), 2, '0')}:${(0, padStart_1.default)(String(second), 2, '0')}`;
    }
    else {
        return `${hour}:${(0, padStart_1.default)(String(minute), 2, '0')}`;
    }
};
exports.secondsSinceMidnightToTimeStr = secondsSinceMidnightToTimeStr;
const decimalHourToTimeStr = function (decimalHour, has24hours = true, withSeconds = false) {
    decimalHour = parseFloat(decimalHour);
    if ((0, isFinite_1.default)(decimalHour) && decimalHour >= 0) {
        const seconds = Math.round(decimalHour * 3600);
        return secondsSinceMidnightToTimeStr(seconds, has24hours, withSeconds);
    }
    return null;
};
exports.decimalHourToTimeStr = decimalHourToTimeStr;
const timeStrToDecimalHour = function (timeStr) {
    if ((0, isString_1.default)(timeStr)) {
        const splittedTime = timeStr.split(':');
        if (splittedTime.length === 2) {
            const hour = parseInt(splittedTime[0]);
            const minute = parseInt(splittedTime[1]);
            if ((0, isFinite_1.default)(hour) && (0, isFinite_1.default)(minute) && hour >= 0 && minute >= 0 && minute < 60) {
                return hour + minute / 60;
            }
        }
    }
    return null;
};
exports.timeStrToDecimalHour = timeStrToDecimalHour;
const secondsToMinutes = function (seconds, rounding = Math.ceil) {
    seconds = parseInt(seconds);
    return (0, isFinite_1.default)(seconds) ? rounding(seconds / 60) : null;
};
exports.secondsToMinutes = secondsToMinutes;
const secondsToMinutesDecimal = function (seconds) {
    seconds = parseInt(seconds);
    return (0, isFinite_1.default)(seconds) ? seconds / 60 : null;
};
exports.secondsToMinutesDecimal = secondsToMinutesDecimal;
const secondsToHours = function (seconds, rounding = Math.ceil) {
    seconds = parseInt(seconds);
    return (0, isFinite_1.default)(seconds) ? rounding(seconds / 3600) : null;
};
exports.secondsToHours = secondsToHours;
const secondsToHoursDecimal = function (seconds) {
    seconds = parseInt(seconds);
    return (0, isFinite_1.default)(seconds) ? seconds / 3600 : null;
};
exports.secondsToHoursDecimal = secondsToHoursDecimal;
const minutesToHours = function (minutes, rounding = Math.ceil) {
    minutes = typeof minutes === 'string' ? parseInt(minutes) : minutes;
    return (0, isFinite_1.default)(minutes) ? rounding(minutes / 60) : null;
};
exports.minutesToHours = minutesToHours;
const minutesToHoursDecimal = function (minutes) {
    minutes = typeof minutes === 'string' ? parseInt(minutes) : minutes;
    return (0, isFinite_1.default)(minutes) ? minutes / 60 : null;
};
exports.minutesToHoursDecimal = minutesToHoursDecimal;
const minutesToSeconds = function (minutes) {
    minutes = typeof minutes === 'string' ? parseInt(minutes) : minutes;
    return (0, isFinite_1.default)(minutes) ? minutes * 60 : null;
};
exports.minutesToSeconds = minutesToSeconds;
const hoursToSeconds = function (hours) {
    hours = parseInt(hours);
    return (0, isFinite_1.default)(hours) ? hours * 3600 : null;
};
exports.hoursToSeconds = hoursToSeconds;
/**
 * Convert a duration in seconds to a formatted time string.
 * The function takes a duration in seconds and returns a string
 * in the format "XX hr YY min ZZ sec", where each non-zero
 * component is included with its corresponding unit.
 *
 * @param {number} seconds - The duration in seconds to be converted.
 * @param {string} hourUnit - The unit for hours (e.g., "hr").
 * @param {string} minuteUnit - The unit for minutes (e.g., "min").
 * @param {string} secondsUnit - The unit for seconds (e.g., "sec").
 * @returns {string} - The formatted time string or an empty string if the input is invalid.
 */
const toXXhrYYminZZsec = function (seconds, hourUnit, minuteUnit, secondsUnit) {
    if ((0, LodashExtensions_1._isBlank)(seconds)) {
        return null;
    }
    if (seconds === 0) {
        return `0 ${secondsUnit}`;
    }
    const negative = seconds < 0;
    const hours = Math.floor(Math.abs(seconds / 3600));
    const minutes = Math.floor(Math.abs((seconds % 3600) / 60));
    const secs = Math.floor(Math.abs(seconds % 60));
    const timeComponents = [
        { value: hours, unit: hourUnit },
        { value: minutes, unit: minuteUnit },
        { value: secs, unit: secondsUnit }
    ];
    return `${negative ? '-' : ''}${timeComponents
        .map((component) => (component.value > 0 ? component.value + ' ' + component.unit : ''))
        .filter(Boolean) // Discard empty components (ex: no seconds)
        .join(' ')}`;
};
exports.toXXhrYYminZZsec = toXXhrYYminZZsec;
/**
 * Convert a time string (HH:MM[:ss]) to the number of seconds since midnight.
 * Time strings can be > 24:00, for example for schedules for a day that end
 * after midnight. The number since midnight will thus be greater than the
 * number of seconds in a day.
 * @param timeStr Number of seconds since midnight (HH:MM[:ss])
 * @returns The number of seconds since midnight, or null if invalid
 */
const timeStrToSecondsSinceMidnight = function (timeStr) {
    if ((0, LodashExtensions_1._isBlank)(timeStr)) {
        return null;
    }
    const splittedTime = timeStr.split(':');
    if (splittedTime.length < 2 || splittedTime.length > 3) {
        return null;
    }
    const minuteStr = splittedTime[1].length < 2 ? '0' + splittedTime[1] : splittedTime[1];
    const secondStr = splittedTime[2] ? (splittedTime[2].length < 2 ? '0' + splittedTime[2] : splittedTime[2]) : '00'; // optional seconds
    const result = Number(splittedTime[0]) * 3600 + Number(minuteStr) * 60 + Number(secondStr);
    return !Number.isNaN(result) ? result : null;
};
exports.timeStrToSecondsSinceMidnight = timeStrToSecondsSinceMidnight;
const intTimeToSecondsSinceMidnight = function (intTime) {
    // 0 = midnight, 100 = 1:00 (AM), 2358 = 23:58, 2743 = 27:43
    if ((0, LodashExtensions_1._isBlank)(intTime)) {
        return null;
    }
    intTime = parseInt(intTime);
    if ((0, isFinite_1.default)(intTime)) {
        const paddedStrTime = (0, padStart_1.default)(intTime.toString(), 4, '0');
        const hour = parseInt(paddedStrTime.slice(0, 2));
        const minute = parseInt(paddedStrTime.slice(2, 4));
        if ((0, isFinite_1.default)(hour) && (0, isFinite_1.default)(minute)) {
            return hour * 3600 + minute * 60;
        }
    }
    return null;
};
exports.intTimeToSecondsSinceMidnight = intTimeToSecondsSinceMidnight;
// returns value as seconds, rounded, ceiled or floored to the nearest 60 seconds:
const roundSecondsToNearestMinute = function (seconds, rounding = Math.ceil) {
    return rounding(seconds / 60) * 60;
};
exports.roundSecondsToNearestMinute = roundSecondsToNearestMinute;
//# sourceMappingURL=DateTimeUtils.js.map